import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import {
  listResmgrHosts,
  listHypervisors,
  enableMaintenanceMode,
  getVmsForHost,
} from '../../actions'
import { simpleMigrateVirtualMachine, simpleAddVmTag } from 'openstack/components/vms/actions'
import { resmgrHostsSelector, hypervisorsSelector, UNASSIGNED_CLUSTER } from '../../selectors'
import { listImages } from 'openstack/components/images/actions'
import { vtpmImagesSelector } from 'openstack/components/images/selectors'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { RoleStatusesCell, IpAddressCellComponent } from '../HostsListPage'
import StatsCell from 'app/plugins/infrastructure/components/common/cells/StatsCell'
import ExternalLink from 'core/components/ExternalLink'
import { v4 as uuid } from 'uuid'
import { maintenanceModeDoc } from 'app/docLinks'
import {
  VALID_MIGRATION_STATES,
  VALID_COLD_MIGRATION_STATES,
} from 'openstack/components/vms/MigrateVirtualMachineDialog'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
  migrations: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  migrationHeader: {
    textAlign: 'center',
  },
  migratingVms: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr 1fr 1fr',
    gap: '8px 32px',
    paddingBottom: 16,
  },
  centered: {
    textAlign: 'center',
  },
  blockingVmsTable: {
    display: 'inline-grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 24px',
    marginTop: 16,
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
}))

interface Props {
  host: any
  onClose: (boolean) => void
  tag: string
}

const hostColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'roleStatuses',
    label: 'Role Status',
    render: (value, item) => <RoleStatusesCell value={value} item={item} />,
  },
  {
    key: 'responding',
    label: 'Connection Status',
  },
  {
    id: 'interfaces',
    label: 'IP Address',
    render: (value) => <IpAddressCellComponent value={value} />,
  },
  {
    id: 'clusterName',
    label: 'cluster',
  },
  {
    id: 'usage',
    label: 'Resource Utilization',
    render: (value) => <StatsCell value={value} />,
  },
  {
    id: 'displayRoles',
    label: 'Assigned Roles',
    render: (values) => values.join(', '),
  },
]

export default function RetryMigrationsDialog({ host, onClose, tag }: Props) {
  const classes = useStyles()
  const [loadingVms, setLoadingVms] = useState(true)
  const [vmsOnHost, setVmsOnHost] = useState([])
  const [submittingMigrations, setSubmittingMigrations] = useState(false)

  const reloadVmsOnHost = useCallback(() => {
    const makeCalls = async () => {
      const vms = await getVmsForHost(host.id)
      setVmsOnHost(vms)
      setLoadingVms(false)
    }
    makeCalls()
  }, [host])

  const allowMaintenance = useMemo(() => {
    if (loadingVms) {
      return false
    }
    return true
  }, [loadingVms, vmsOnHost])

  const { loading: loadingImages } = useListAction(listImages)
  const vtpmImages = useSelector(vtpmImagesSelector)

  useEffect(() => {
    // Get initial number of VMs on the host to determine
    // if migrations are necessary
    reloadVmsOnHost()
  }, [])

  const defaultParams = {
    selectHost: false,
    skipPoweredOffVms: false,
    host: [],
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { message, loading: loadingResmgrHosts, reload } = useListAction(listResmgrHosts, {
    params,
  })

  const allHosts = useSelector(resmgrHostsSelector)
  const { loading: loadingHypervisors, reload: reloadHypervisors } = useListAction(listHypervisors)
  const hypervisors = useSelector(hypervisorsSelector)

  const validHosts = useMemo(() => {
    if (!allHosts.length || !host || !hypervisors.length) {
      return []
    }
    const hypervisorsByResmgrId = hypervisors.reduce((accum, hypervisor) => {
      return {
        ...accum,
        [hypervisor?.service?.host]: hypervisor,
      }
    }, {})
    const hostsWithHypervisor = allHosts.map((host) => {
      return {
        ...host,
        hypervisor: hypervisorsByResmgrId[host.id],
      }
    })

    return hostsWithHypervisor.filter((resmgrHost) => {
      return (
        resmgrHost.id !== host.id &&
        resmgrHost.hypervisor?.state === 'up' &&
        resmgrHost.hypervisor?.status === 'enabled' &&
        // If the host is unassigned, then it can be moved to any cluster
        // If the host is assigned to a cluster, then it can only be moved to host with the same cluster or unassigned host
        (host?.clusterName === UNASSIGNED_CLUSTER ||
          resmgrHost?.clusterName === host?.clusterName ||
          resmgrHost?.clusterName === UNASSIGNED_CLUSTER)
      )
    })
  }, [allHosts, host, hypervisors])

  const {
    update: disableHypervisor,
    updating: disablingHypervisor,
    error: errorDisablingHypervisor,
    reset: resetDisableHypervisor,
  } = useUpdateAction(enableMaintenanceMode)

  const triggerMigrations = useCallback(async () => {
    setSubmittingMigrations(true)

    const validVms = vmsOnHost.filter((vm) => {
      // Skip active vTPM vms
      if (['active', 'paused'].includes(vm['OS-EXT-STS:vm_state'])) {
        if (vm?.flavor?.extra_specs?.tpm_version) {
          return false
        }
        if (vm?.image?.id) {
          if (vtpmImages.some((image) => image?.id === vm?.image?.id)) {
            return false
          }
        }
      }
      // Skip powered off VMs if flag toggled
      return params.skipPoweredOffVms
        ? ['active', 'paused', 'suspended'].includes(vm['OS-EXT-STS:vm_state'])
        : VALID_MIGRATION_STATES.includes(vm['OS-EXT-STS:vm_state'])
    })

    const tagPromises = validVms.map((vm) => simpleAddVmTag({ id: vm.id, tag }))
    const tagResults = await Promise.allSettled(tagPromises)
    // If any VMs were not tagged properly and they are migrated off the host, then will lose history that they were part of the migration.
    // const failedTagIndexes = tagResults
    //   ?.map((result, idx) => (result.status === 'rejected' ? idx : null))
    //   ?.filter((idx) => idx !== null)
    // const failedTagVms = failedTagIndexes.map((idx) => validVms[idx])
    // Potentially show something regarding the VMs that didn't get tagged

    const promises = validVms.map((vm) => {
      const body = VALID_COLD_MIGRATION_STATES.includes(vm['OS-EXT-STS:vm_state'])
        ? {
            migrate: {
              host: params.selectHost ? params.host[0]?.id : null,
            },
          }
        : {
            'os-migrateLive': {
              host: params.selectHost ? params.host[0]?.id : null,
              block_migration: 'auto',
            },
          }
      return simpleMigrateVirtualMachine({ id: vm.id, body })
    })
    const results = await Promise.allSettled(promises)
    const acceptedMigrationIndexes = results
      ?.map((result, idx) => (result.status === 'fulfilled' ? idx : null))
      ?.filter((idx) => idx !== null)
    // If we need to show which VMs were successfully triggered in the future
    // const vmsWithMigrationTriggered = acceptedMigrationIndexes.map((idx) => validVms[idx])

    handleClose(true)
  }, [params, host, vmsOnHost, vtpmImages, tag])

  const handleClose = (success?: boolean) => {
    resetDisableHypervisor()
    // reloadVms(true)
    onClose(success)
  }

  return (
    <ModalForm
      title={'Retry VM Migrations'}
      onSubmit={triggerMigrations}
      onClose={() => handleClose()}
      loading={loadingVms || loadingImages}
      submitting={disablingHypervisor || submittingMigrations}
      error={errorDisablingHypervisor}
      submitTitle="Retry VM Migrations"
      panel="dialog"
      maxWidth={1200}
      disableSubmit={!allowMaintenance}
      open
    >
      <div className={classes.fields}>
        {allowMaintenance ? (
          <>
            <Text variant="body2">
              You are about to migrate remaining VMs on host <b>{host?.name}</b>.
            </Text>
            <Text variant="body2">
              There are currently <b>{vmsOnHost?.length}</b> remaining VM(s) on this host.
            </Text>
            <Text variant="body2">
              <b>NOTE:</b> If you have any vTPM VMs, please shut off these VMs prior to migration.
              Active vTPM VMs detected will be skipped.
            </Text>
            <ToggleSwitchField
              id="skipPoweredOffVms"
              label="Skip migration of powered off VMs"
              value={params.skipPoweredOffVms}
              onChange={getParamsUpdater('skipPoweredOffVms')}
              customTooltipInfoBody={
                <Text variant="body2" className={classes.switchTooltip}>
                  If enabled, powered off (stopped) VMs will not be migrated.
                </Text>
              }
            />
            <ToggleSwitchField
              id="selectHost"
              label="Designate a specific host to migrate VMs to"
              value={params.selectHost}
              onChange={getParamsUpdater('selectHost')}
              customTooltipInfoBody={
                <Text variant="body2" className={classes.switchTooltip}>
                  If enabled, select a host to which all active VMs on this host will be migrated
                  to. If left disabled, VMs will be distributed to new hosts automatically.
                </Text>
              }
            />
            {params.selectHost && (
              <ListTableField
                id="host"
                data={validHosts}
                loading={loadingResmgrHosts || loadingHypervisors}
                columns={hostColumns}
                onChange={getParamsUpdater('host')}
                value={params.host}
                uniqueIdentifier="id"
                searchTargets={['name']}
                noRowsPerPage
                required
              />
            )}
          </>
        ) : (
          <>
            <Text variant="body2">
              You are about to migrate remaining VMs on host <b>{host.name}</b>. Refer to this{' '}
              <ExternalLink url={maintenanceModeDoc}>article</ExternalLink> for more information.
            </Text>
          </>
        )}
      </div>
    </ModalForm>
  )
}
